import { useMemo } from 'react';

import { DataTable, type TableColumn } from '@/src/ui';
import { SimpleButton } from '@/src/ui';
import type { Colors } from '@/src/ui/styling';
import type { ExamAssignmentDTO } from '../StudentDashboardDTOs';
import { ReportHeaderStyle, ReportCellStyle, utcToLocal, formatOptions } from '@/src/utils';

interface ExamAssignmentsTableProps {
    data: Array<ExamAssignmentDTO>;
    tableActionOnClick: (createNewAttempt: boolean, actionId: number) => void;
}

export default function ExamAssignmentsTable(props: ExamAssignmentsTableProps) {
    const { data, tableActionOnClick } = props;

    const columns = useMemo<TableColumn[]>(() => {
        return [
            {
                dataKey: 'title',
                header: 'Assessment',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'toDate',
                header: 'Available Until',
                align: 'left',
                width: 100,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row) => {
                    return row.toDate
                        ? utcToLocal(row.toDate)?.toLocaleString(undefined, formatOptions)
                        : '--';
                },
            },
            {
                dataKey: 'actions',
                header: 'Actions',
                align: 'left',
                width: 150,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: ExamAssignmentDTO) => {
                    let label = '';
                    let onClick = () => {};
                    let color: Colors = 'primary';
                    let disabled = false;

                    const attempts = row.attempts || [];

                    const completedAttempts = attempts.filter(
                        (a) => a.submittedStatus === 3
                    ).length;

                    const activeAttempt = attempts.find((a) => a.submittedStatus !== 3);

                    if (activeAttempt && activeAttempt.isTeacherFreezeEnabled) {
                        // Teacher freeze enabled, cannot continue
                        label = 'Frozen by Teacher';
                        disabled = true;
                    } else if (attempts.length === 0) {
                        // No attempts exist for some reason, create one
                        label = 'Begin';
                        color = 'success';
                        onClick = () => tableActionOnClick(true, row.id);
                    } else if (completedAttempts >= (row.numberOfAttemptsAllowed || 1)) {
                        // All available attempts used
                        label = 'Completed';
                        disabled = true;
                    } else if (activeAttempt) {
                        // Attempt not started or in progress
                        onClick = () => tableActionOnClick(false, activeAttempt.id);

                        if (activeAttempt.submittedStatus === 1) {
                            color = 'success';
                            label = 'Begin';
                        } else if (activeAttempt.submittedStatus === 2) {
                            color = 'warning';
                            label = 'Resume';
                        }
                    } else {
                        label = 'Take Again';
                        color = 'warning';
                        onClick = () => tableActionOnClick(true, row.id);
                    }

                    return (
                        <SimpleButton
                            disabled={disabled}
                            onClick={onClick}
                            color={color}
                            variant="contained"
                        >
                            {label}
                        </SimpleButton>
                    );
                },
            },
        ];
    }, [tableActionOnClick]);

    return (
        <div style={{ overflowX: 'auto', width: '100%' }}>
            <DataTable
                data={data}
                columns={columns}
                tableName="Assessments"
                alternatingRowColor
                rowsPerPageInitial={10}
                rowsPerPageList={[10]}
                defaultSortColumn="title"
                defaultSortDirection="asc"
            />
        </div>
    );
}
